import { Contract } from '@ethersproject/contracts'
import { getAddress } from '@ethersproject/address'
import { AddressZero } from '@ethersproject/constants'
import { JsonRpcSigner, Web3Provider } from '@ethersproject/providers'
import { BigNumber } from '@ethersproject/bignumber'
import { ChainId, Percent, Token, CurrencyAmount, Currency, ETHER } from '@uniswap/sdk-core'
import { JSBI } from '@ambidex/sdk'
import { FeeAmount } from '@uniswap/v3-sdk/dist/'
import { TokenAddressMap } from '../state/lists/hooks'
import { AmbidexChainId } from 'constants/networks'
import whiteListAccounts from '../assets/json/white-list-accounts.json'

// returns the checksummed address if the address is valid, otherwise returns false
export function isAddress(value: any): string | false {
  try {
    return getAddress(value)
  } catch {
    return false
  }
}

const ETHERSCAN_PREFIXES: { [chainId in ChainId]: string } = {
  1: '',
  3: 'ropsten.',
  4: 'rinkeby.',
  5: 'goerli.',
  42: 'kovan.',
}

export function getHashscanLink(
  chainId: AmbidexChainId,
  data: string,
  type: 'transaction' | 'token' | 'address' | 'block'
): string {
  // TODO: handle multiple networks(testnet, mainnet, previewnet)
  const prefix = `https://hashscan.io/${
    AmbidexChainId.HEDERA_MAINNET === chainId
      ? 'mainnet'
      : AmbidexChainId.HEDERA_TESTNET === chainId
      ? 'testnet'
      : undefined
  }`

  switch (type) {
    case 'transaction': {
      const [accountId, txDetail] = data.split('@')
      return `${prefix}/transaction/${accountId}-${txDetail.replace('.', '-')}`
    }
    case 'token': {
      return `${prefix}/token/${data}`
    }
    case 'block': {
      return `${prefix}/block/${data}`
    }
    case 'address':
    default: {
      return `${prefix}/account/${data}`
    }
  }
}

// shorten the checksummed version of the input address to have 0x + 4 characters at start and end
export function shortenAddress(address: string, chars = 4): string {
  const parsed = isAddress(address)
  if (!parsed) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }
  return `${parsed.substring(0, chars + 2)}...${parsed.substring(42 - chars)}`
}

// add 10%
export function calculateGasMargin(value: BigNumber): BigNumber {
  return value.mul(BigNumber.from(10000).add(BigNumber.from(1000))).div(BigNumber.from(10000))
}

export function calculateSlippageAmount(value: CurrencyAmount, slippage: Percent): [JSBI, JSBI] {
  if (JSBI.lessThan(slippage.numerator, JSBI.BigInt(0)) || JSBI.greaterThan(slippage.numerator, JSBI.BigInt(10_000)))
    throw new Error('Unexpected slippage')
  return [
    JSBI.divide(JSBI.multiply(value.raw, JSBI.subtract(JSBI.BigInt(10000), slippage.numerator)), JSBI.BigInt(10000)),
    JSBI.divide(JSBI.multiply(value.raw, JSBI.add(JSBI.BigInt(10000), slippage.numerator)), JSBI.BigInt(10000)),
  ]
}

// account is not optional
export function getSigner(library: Web3Provider, account: string): JsonRpcSigner {
  return library.getSigner(account).connectUnchecked()
}

// account is optional
export function getProviderOrSigner(library: Web3Provider, account?: string): Web3Provider | JsonRpcSigner {
  return account ? getSigner(library, account) : library
}

// account is optional
export function getContract(address: string, ABI: any, library: Web3Provider, account?: string): Contract {
  if (!isAddress(address) || address === AddressZero) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }

  return new Contract(address, ABI, getProviderOrSigner(library, account) as any)
}

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export function isTokenOnList(defaultTokens: TokenAddressMap, currency?: Currency): boolean {
  if (currency === ETHER) return true
  return Boolean(currency instanceof Token && defaultTokens[currency.chainId as ChainId]?.[currency.address])
}

/**
 * Returns chain id if chain from chainId supports WETH
 * if not, return undefined
 * @param chainId
 */
export function supportedChainId(chainId: number): ChainId | undefined {
  if (chainId in ChainId) {
    return chainId
  }
  return undefined
}

export function formattedFeeAmount(feeAmount: FeeAmount): number {
  return feeAmount / 10000
}

export function isAccountWhitelisted(account: string): boolean {
  const accounts =
    process.env.REACT_APP_CHAIN_ID === AmbidexChainId.HEDERA_TESTNET.toString()
      ? whiteListAccounts['testnet']
      : process.env.REACT_APP_CHAIN_ID === AmbidexChainId.HEDERA_MAINNET.toString()
      ? whiteListAccounts['mainnet']
      : []
  return accounts.includes(account)
}

export function getLpTokenSymbol(currencyA: Currency | null | undefined, currencyB: Currency | null | undefined) {
  if (!currencyA || !currencyB) return ''
  return (
    'amLP' +
    '-' +
    ((currencyA as Token)?.sortsBefore(currencyB as Token) ? currencyA?.symbol : currencyB?.symbol) +
    '-' +
    ((currencyA as Token)?.sortsBefore(currencyB as Token) ? currencyB?.symbol : currencyA?.symbol)
  ).replace('HBAR', 'wHBAR')
}
